/**
 * Edad mínima para registrarse en Aguacate: 18 años cumplidos, para cualquier
 * nacionalidad.
 *
 * El control que manda está en el servidor (el front deja editar cosas y se
 * puede saltear); acá solo se corta temprano, en el paso del OCR, para no
 * hacerle sacar la selfie y declarar PEP a alguien que igual va a ser rechazado.
 * Tiene que coincidir con `src/common/utils/edad.util.ts` del KYC backend.
 */
export const EDAD_MINIMA = 18;

/** Zona con la que se decide qué día es hoy; la misma que usa el KYC */
export const ZONA_HORARIA_EDAD = 'America/Santiago';

export const MENSAJE_MENOR_DE_EDAD =
  'Para registrarte en Aguacate debes tener al menos 18 años cumplidos. ' +
  'Si crees que es un error, contáctanos.';

export const MENSAJE_REPRESENTANTE_MENOR_DE_EDAD =
  'El representante legal debe tener al menos 18 años cumplidos. ' +
  'Si crees que es un error, contáctanos.';

export const MENSAJE_FECHA_NACIMIENTO_ILEGIBLE =
  'No pudimos leer tu fecha de nacimiento. Toma otra foto del documento con buena luz y sin reflejos.';

/** Prefijo del aviso que agrega el KYC cuando leyó la fecha pero no la pudo interpretar */
const AVISO_FECHA_ILEGIBLE = 'Fecha de nacimiento ilegible';

const FECHA_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Años cumplidos a partir de `AAAA-MM-DD`, o null si la fecha no es válida.
 *
 * Trabaja sobre el texto: `new Date('AAAA-MM-DD')` es medianoche UTC y leído
 * con hora local de Chile o Venezuela cae en el día anterior.
 */
export function edadCumplida(fechaISO: string | null | undefined, ahora = new Date()): number | null {
  const partes = FECHA_ISO.exec(fechaISO ?? '');
  if (!partes) return null;

  const anio = Number(partes[1]);
  const mes = Number(partes[2]);
  const dia = Number(partes[3]);
  const control = new Date(Date.UTC(anio, mes - 1, dia));
  if (
    control.getUTCFullYear() !== anio ||
    control.getUTCMonth() !== mes - 1 ||
    control.getUTCDate() !== dia
  ) {
    return null;
  }

  const [hoyAnio, hoyMes, hoyDia] = hoyEnZona(ahora);

  let edad = hoyAnio - anio;
  if (hoyMes < mes || (hoyMes === mes && hoyDia < dia)) edad--;
  return edad;
}

/** Hoy en la zona de referencia, como `[año, mes, día]` */
function hoyEnZona(ahora: Date): [number, number, number] {
  const [anio, mes, dia] = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA_HORARIA_EDAD,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(ahora)
    .split('-')
    .map(Number);
  return [anio, mes, dia];
}

/**
 * Fecha de nacimiento más reciente que todavía cumple la edad mínima, como
 * `AAAA-MM-DD`, para el `max` de los `<input type="date">`. Un 29 de febrero
 * sin bisiesto 18 años atrás baja al 28.
 */
export function fechaNacimientoMaxima(ahora = new Date()): string {
  const [anio, mes, dia] = hoyEnZona(ahora);
  const tope = anio - EDAD_MINIMA;
  const ultimoDiaDelMes = new Date(Date.UTC(tope, mes, 0)).getUTCDate();
  const d = Math.min(dia, ultimoDiaDelMes);
  return `${tope}-${String(mes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Sin fecha válida devuelve false: no se puede afirmar la edad de nadie sin ella */
export function cumpleEdadMinima(fechaISO: string | null | undefined, ahora = new Date()): boolean {
  const edad = edadCumplida(fechaISO, ahora);
  return edad !== null && edad >= EDAD_MINIMA;
}

/**
 * Motivo por el que la respuesta del OCR no deja seguir por la edad, o null.
 *
 * Hay que mirarlo antes que `camposCompletos`: el KYC también lo pone en false
 * para un menor, y sin esto el usuario veía "Faltan datos… captura el documento
 * completo" y reintentaba sin entender por qué.
 */
export function motivoEdadEnOcr(response: any, esRepresentante = false): string | null {
  const validacion = response?.validacion;
  const fechaISO = response?.campos?.fechaNacimientoISO;

  const edad = edadCumplida(fechaISO);
  if (fechaISO && (validacion?.esMayorDeEdad === false || (edad !== null && edad < EDAD_MINIMA))) {
    return esRepresentante ? MENSAJE_REPRESENTANTE_MENOR_DE_EDAD : MENSAJE_MENOR_DE_EDAD;
  }

  const advertencias: unknown[] = validacion?.advertencias ?? [];
  if (advertencias.some((a) => typeof a === 'string' && a.includes(AVISO_FECHA_ILEGIBLE))) {
    return MENSAJE_FECHA_NACIMIENTO_ILEGIBLE;
  }

  return null;
}
